// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
/* istanbul ignore file */

import { type AxiosError } from 'axios';
import { useConfirmPopup } from '@shared/ui-kit/components/popups';
import { messages } from '@shared/ui-kit/utils/handled-error-messages';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/* istanbul ignore next */
export const useHandleApiErrors = () => {
  const showConfirm = useConfirmPopup();
  const { t } = useTranslation();

  const handleError = useCallback(
    (error: AxiosError) => {
      const { code, config } = error;
      const { method, url } = config ?? {};
      const handledEndpoint =
        method && url ? messages.get(`${method}-${url}`) : undefined;
      const isTimeout = code === 'ECONNABORTED';

      const message =
        handledEndpoint ||
        (isTimeout && (t('common.timeout-error', { ns: 'common' }) as string)) ||
        (t('common.general-error', { ns: 'common' }) as string);

      showConfirm({
        title: t('common.unable-to-load', { ns: 'common' }) as string,
        message,
        confirmText: t('common.reload', { ns: 'common' }) as string,
        onConfirm: () => window.location.reload(),
        cancelText: t('common.home', { ns: 'common' }) as string,
        onCancel: () => window.location.assign('/'),
      });
    },
    [showConfirm, t],
  );

  return handleError;
};
